import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { LANGUAGES, applyLanguage } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { setPreferredLanguage } from "@/lib/api";

export function LanguageSelector() {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES.find((l) => l.code === "it")!;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    listRef.current?.querySelector<HTMLButtonElement>('[aria-selected="true"]')?.focus();
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (code: string) => {
    applyLanguage(code);
    setOpen(false);
    if (user) setPreferredLanguage(code).catch(() => {});
  };

  const onListKey = (e: React.KeyboardEvent<HTMLUListElement>) => {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "ArrowDown") { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === "ArrowUp") { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="language-selector-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("common.language")}
        title={t("common.language")}
        onClick={() => setOpen((v) => !v)}
        className="glass flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-bold tracking-wider text-foreground !shadow-none transition-[color,border-color] hover:text-[#7FD4FF]"
      >
        <span data-testid="language-current" className="max-w-[92px] truncate">{current.name}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} strokeWidth={2} />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label={t("common.language")}
          data-testid="language-list"
          onKeyDown={onListKey}
          className="glass-panel absolute right-0 top-11 z-50 max-h-[70vh] w-48 overflow-y-auto rounded-xl p-1.5"
        >
          {LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  lang={l.code}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => choose(l.code)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left text-sm transition-[background-color,color] focus:outline-none focus-visible:bg-[#2E7BFF]/15",
                    active ? "bg-[#2E7BFF]/15 text-[#7FD4FF]" : "text-foreground hover:bg-[#2E7BFF]/10",
                  )}
                >
                  {l.name}
                  {active && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
